import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Mail, MessageSquare, FileSpreadsheet } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");

const Table = ({ cols, rows, empty, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr>{cols.map((c) => <th key={c} className="p-3">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={cols.length} className="p-8 text-center text-slate-400">{empty}</td></tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {r.map((cell, j) => <td key={j} className="p-3 max-w-xs truncate">{cell}</td>)}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const CustomersTab = () => {
  const [view, setView] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/customers").then((r) => setCustomers(r.data)),
      api.get("/admin/newsletter").then((r) => setSubs(r.data)),
      api.get("/admin/contact-messages").then((r) => setMsgs(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const tabs = [
    { k: "customers", label: "Accounts", icon: Users, count: customers.length },
    { k: "newsletter", label: "Newsletter", icon: Mail, count: subs.length },
    { k: "messages", label: "Messages", icon: MessageSquare, count: msgs.length },
  ];

  const exportPurchaseList = async () => {
    try {
      const r = await api.get("/admin/customers/purchase-list.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "dealswagon-customers.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Customer list downloaded");
    } catch { toast.error("Could not export customer list"); }
  };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-2xl text-brand-navy">Customers</div>
        <button onClick={exportPurchaseList} data-testid="customers-export-excel-btn" className="inline-flex items-center gap-1 bg-brand-navy text-white rounded-full h-10 px-4 font-bold text-sm">
          <FileSpreadsheet className="h-4 w-4" /> Download Excel
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {tabs.map(({ k, label, icon: Icon, count }) => (
          <button key={k} onClick={() => setView(k)} data-testid={`customers-subtab-${k}`}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-bold ${view === k ? "bg-brand-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            <Icon className="h-4 w-4" /> {label} <span className="opacity-70">({count})</span>
          </button>
        ))}
      </div>
      {loading ? <div className="text-slate-400">Loading...</div> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {view === "customers" && <Table testid="customers-table" cols={["Name", "Email", "Joined"]}
            rows={customers.map((c) => [c.name || "—", c.email, fmt(c.created_at)])}
            empty="No customer accounts yet. They appear here as people sign in." />}
          {view === "newsletter" && <Table testid="newsletter-table" cols={["Name", "Email", "Signed up"]}
            rows={subs.map((c) => [c.name || "—", c.email, fmt(c.created_at)])}
            empty="No newsletter sign-ups yet." />}
          {view === "messages" && <Table testid="messages-table" cols={["Name", "Email", "Phone", "Message", "Received"]}
            rows={msgs.map((c) => [c.name, c.email, c.phone || "—", c.message, fmt(c.created_at)])}
            empty="No contact messages yet." />}
        </div>
      )}
    </div>
  );
};
